import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { destinoTrasLogin, irAlDestino } from '../lib/homeRoute';
import { candidatosCorreoDeAcceso, esCorreoDeFuera, pareceDocumento, pareceUsuario } from '../domain/loginIdentity';
import MarcaTreidOps from '../components/MarcaTreidOps';
import { ingresarComoOficina } from '../api/loginOficina';
import CampoClave from '../components/CampoClave';

/** Nunca revela si el documento existe: el mismo texto para "no existe" y "clave errada". */
const ERROR_DOCUMENTO =
  'Documento o clave incorrectos. Si es tu primer ingreso, usa la clave que te dio tu coordinador.';

const credencialInvalida = (msg: string): boolean => /Invalid login credentials/i.test(msg);

export default function Login() {
  const [identidad, setIdentidad] = useState('');
  const [password, setPassword] = useState('');
  const [tecladoCorreo, setTecladoCorreo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Lo que se envía se lee DEL FORMULARIO, no del estado de React.
    //
    // POR QUÉ: los dos campos son controlados, y cuando el gestor de contraseñas del navegador
    // los rellena —el usuario toca el campo, aprueba con su huella y Chrome pone usuario y
    // clave— ese relleno no siempre dispara el evento que React escucha. El campo MUESTRA el
    // texto pero el estado sigue vacío, así que el envío salía sin credenciales y, al repintar,
    // React borraba de la pantalla lo que el navegador había puesto: "le doy Ingresar, no pasa
    // nada y desaparecen mis credenciales". `FormData` lee lo que de verdad hay en el campo,
    // venga de donde venga.
    const datos = new FormData(e.currentTarget as HTMLFormElement);
    const identidadReal = String(datos.get('identidad') ?? '').trim() || identidad;
    const passwordReal = String(datos.get('password') ?? '') || password;

    // Se sincroniza el estado con lo que había en el campo: si no, el primer `setState` de
    // abajo repintaría los inputs vacíos y la persona vería desaparecer lo que ve escrito.
    if (identidadReal !== identidad) setIdentidad(identidadReal);
    if (passwordReal !== password) setPassword(passwordReal);

    // El trabajador escribe su documento; el correo sintético lo arma la app.
    const candidatos = candidatosCorreoDeAcceso(identidadReal);
    if (candidatos.length === 0 || !candidatos[0].includes('@')) {
      setError('Escribe tu número de documento o tu correo.');
      return;
    }
    const esDocumento = pareceDocumento(identidadReal);

    setSending(true);
    // Puede haber dos candidatos (documento con cero a la izquierda): se prueban en orden y
    // solo se insiste mientras el rechazo sea por credenciales.
    let user: { id: string } | null = null;
    let authMessage = '';
    for (const correo of candidatos) {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: correo,
        password: passwordReal,
      });
      if (!authError && data?.user) {
        user = data.user;
        authMessage = '';
        break;
      }
      authMessage = authError?.message ?? '';
      if (!credencialInvalida(authMessage)) break;
    }

    // Segundo intento SOLO para el personal de oficina que escribió su correo real: la cuenta se
    // autentica contra `<documento>@campo.treid.pe` y Supabase admite un correo por cuenta, así
    // que `npatino@treid.pe` no abre nada por sí solo. La traducción la hace el servidor
    // (`login-oficina`) para no publicar el mapeo correo → documento en el JavaScript, donde el
    // documento ES el nombre de usuario. Va DESPUÉS del camino normal a propósito: si la función
    // falla, todo el mundo sigue entrando como hoy.
    if (!user && credencialInvalida(authMessage) && esCorreoDeFuera(identidadReal)) {
      if (await ingresarComoOficina(identidadReal, passwordReal)) {
        const { data } = await supabase.auth.getUser();
        if (data?.user) user = { id: data.user.id };
      }
    }

    if (!user) {
      setSending(false);
      if (credencialInvalida(authMessage)) {
        setError(
          esDocumento
            ? ERROR_DOCUMENTO
            : pareceUsuario(identidadReal) ? 'Usuario o clave incorrectos.' : 'Correo o clave incorrectos.',
        );
      } else if (/Email not confirmed/i.test(authMessage)) {
        setError('Tu acceso aún no está confirmado. Contacta al jefe de operaciones.');
      } else {
        setError('No se pudo iniciar sesión. Revisa tu conexión e inténtalo de nuevo.');
      }
      return;
    }

    // ⚠️ La sesión se confirma ANTES de leer el perfil.
    //
    // `profiles_select_propio` es `id = auth.uid()`. Si la petición sale sin el token
    // aplicado —el `signInWithPassword` resolvió pero el cliente REST todavía no lo
    // adjunta—, la RLS no encuentra nada y devuelve **cero filas SIN error**. Eso es
    // indistinguible de "no tiene ficha", y por eso el jefe de operaciones podía ver
    // «tu usuario no tiene ficha de trabajador» teniendo la suya perfectamente creada.
    const { data: sesionActual } = await supabase.auth.getSession();
    if (!sesionActual.session) {
      setSending(false);
      setError('Tu sesión no se estableció. Espera unos segundos e inténtalo de nuevo.');
      return;
    }

    const { data: profile, error: errorPerfil } = await supabase
      .from('profiles')
      .select('roles, status, block_reason')
      .eq('id', user.id)
      .maybeSingle();

    // Un fallo de la consulta NO es una ficha inexistente. Antes se descartaba el error y
    // se culpaba al usuario de algo que no era suyo, que es la peor forma de fallar:
    // manda a la persona a pedirle a otro que le arregle un problema que no tiene.
    if (errorPerfil) {
      await supabase.auth.signOut();
      setSending(false);
      setError(
        `No se pudo leer tu ficha (${errorPerfil.code || 'error'}). Vuelve a intentarlo; si sigue, avisa con este código.`,
      );
      return;
    }

    if (!profile) {
      await supabase.auth.signOut();
      setSending(false);
      setError('Tu usuario no tiene ficha de trabajador. Contacta al jefe de operaciones.');
      return;
    }
    if (profile.status === 'bloqueado') {
      await supabase.auth.signOut();
      setSending(false);
      setError(
        `Tu cuenta está bloqueada${profile.block_reason ? `: ${profile.block_reason}` : ''}. Contacta al jefe de operaciones.`,
      );
      return;
    }
    if (profile.status !== 'activo') {
      await supabase.auth.signOut();
      setSending(false);
      setError('Tu cuenta está inactiva. Contacta al jefe de operaciones.');
      return;
    }
    irAlDestino(destinoTrasLogin(profile.roles ?? [], window.location.search), navigate);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm rounded-lg bg-white p-6 shadow">
        <MarcaTreidOps como="h1" tamano="lg" />
        <p className="mb-4 mt-3 text-sm text-gray-500">Inicia sesión con tu documento y clave.</p>
        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="identidad">
          DNI o correo
        </label>
        <input
          id="identidad"
          name="identidad"
          type="text"
          // Teclado numérico por defecto: en campo casi todos entran con su documento.
          // El personal de oficina cambia a teclado de correo con el enlace de abajo (en un
          // celular, el teclado numérico no tiene ni letras ni arroba).
          inputMode={tecladoCorreo || identidad.includes('@') ? 'email' : 'numeric'}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={identidad}
          onChange={(e) => setIdentidad(e.target.value)}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />
        <p className="mb-1 mt-1 text-xs text-gray-500">
          Los trabajadores entran con su número de documento.
        </p>
        <button
          type="button"
          onClick={() => setTecladoCorreo((v) => !v)}
          className="mb-3 text-xs text-blue-600 underline"
        >
          {tecladoCorreo ? 'Volver a ingresar con documento' : 'Soy de oficina: ingresar con correo'}
        </button>
        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="password">
          Clave
        </label>
        <CampoClave
          id="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={setPassword}
          className="mb-4"
        />
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={sending}
          className="w-full rounded bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {sending ? 'Ingresando…' : 'Ingresar'}
        </button>
        <p className="mt-4 text-center text-xs text-gray-500">
          ¿Eres trabajador nuevo?{' '}
          <Link to="/registro" className="text-blue-600 underline">
            Regístrate aquí
          </Link>
        </p>
      </form>
    </div>
  );
}
